import * as React from "react";
import { useAppearance } from "@eduviaos/ui-kit";

// `@monaco-editor/react` is ~2 MB and only needed inside the template
// editor. React.lazy + Suspense (managed by the page-level shell) means
// the bundle only ships when the SA navigates to /settings/email-templates.
const MonacoEditorImpl = React.lazy(async () => {
  const mod = await import("@monaco-editor/react");
  return { default: mod.Editor };
});

export interface MonacoEditorProps {
  value: string;
  onChange: (next: string) => void;
  language: "html" | "plaintext";
  /** Forwarded to Monaco — height in CSS units. */
  height?: string;
  /** Read-only flag for "view this old version" use case. */
  readOnly?: boolean;
  /** Imperative handle: get/set selection so the variables sidebar can
   * insert at the cursor. */
  onMount?: (insertAtCursor: (snippet: string) => void) => void;
  ariaLabel?: string;
}

/**
 * Wrapper around `@monaco-editor/react` that:
 *
 *  - Lazy-loads Monaco only when the editor is actually rendered.
 *  - Switches Monaco's theme in lock-step with the app's resolvedMode.
 *  - Exposes a tiny imperative `insertAtCursor` so the variables sidebar
 *    can drop `{{ name }}` snippets at the cursor without us reaching
 *    into Monaco's APIs from elsewhere.
 *  - Disables minimap + word wrap defaults that don't fit short
 *    transactional templates.
 */
export function MonacoEditor({
  value,
  onChange,
  language,
  height = "320px",
  readOnly = false,
  onMount,
  ariaLabel,
}: MonacoEditorProps) {
  const { resolvedMode } = useAppearance();
  const monacoTheme = resolvedMode === "dark" ? "vs-dark" : "vs";

  const editorRef = React.useRef<unknown>(null);

  const handleMount = React.useCallback(
    (editor: unknown) => {
      editorRef.current = editor;
      if (onMount) {
        onMount((snippet: string) => {
          // Type narrowing: Monaco's Editor type isn't exported cleanly;
          // we only need a small subset (selection + edit), so we duck-type.
          const ed = editorRef.current as null | {
            getSelection: () => unknown;
            executeEdits: (
              source: string,
              edits: { range: unknown; text: string; forceMoveMarkers: boolean }[],
            ) => void;
            focus: () => void;
            getPosition: () => { lineNumber: number; column: number } | null;
          };
          if (!ed) return;
          const pos = ed.getPosition();
          if (!pos) return;
          const range = {
            startLineNumber: pos.lineNumber,
            startColumn: pos.column,
            endLineNumber: pos.lineNumber,
            endColumn: pos.column,
          };
          ed.executeEdits("variables-sidebar", [{ range, text: snippet, forceMoveMarkers: true }]);
          ed.focus();
        });
      }
    },
    [onMount],
  );

  return (
    <div
      className="bg-card overflow-hidden rounded-md border"
      role="region"
      aria-label={ariaLabel ?? "Code editor"}
    >
      <React.Suspense
        fallback={
          <div
            className="text-muted-foreground flex h-[320px] items-center justify-center text-sm"
            aria-live="polite"
          >
            Loading editor…
          </div>
        }
      >
        <MonacoEditorImpl
          height={height}
          language={language}
          value={value}
          theme={monacoTheme}
          onChange={(next) => onChange(next ?? "")}
          onMount={handleMount}
          options={{
            readOnly,
            minimap: { enabled: false },
            wordWrap: "on",
            wrappingIndent: "indent",
            tabSize: 2,
            fontSize: 13,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            renderLineHighlight: "line",
            automaticLayout: true,
            padding: { top: 12, bottom: 12 },
          }}
        />
      </React.Suspense>
    </div>
  );
}
